'use client'

import { useCallback, useEffect, useState } from 'react'
import { Share, X } from 'lucide-react'
import { isSessionActive, onSessionActiveChange } from '@/lib/pwa/session-activity'
import {
  INSTALL_PROMPT_DELAY_MS,
  INSTALL_PROMPT_STORAGE_KEY,
  detectInstallPlatform,
  nextStateAfterDismiss,
  nextStateAfterInstall,
  parseInstallPromptState,
  shouldShowInstallPrompt,
  type InstallPlatform,
  type InstallPromptState,
} from '@/lib/pwa/install-prompt'

/**
 * Chrome's install event. Not in lib.dom, so it's declared here.
 */
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

function readState(): InstallPromptState {
  try {
    return parseInstallPromptState(window.localStorage.getItem(INSTALL_PROMPT_STORAGE_KEY))
  } catch {
    // Safari private mode throws on localStorage access. Treat as "never asked".
    return {}
  }
}

function writeState(state: InstallPromptState): void {
  try {
    window.localStorage.setItem(INSTALL_PROMPT_STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Non-fatal: we just lose nag suppression for this browser.
  }
}

/** True when the page is already running as an installed app. */
function detectStandalone(): boolean {
  if (typeof window === 'undefined') return false
  const displayMode = window.matchMedia?.('(display-mode: standalone)')?.matches ?? false
  // iOS Safari predates the display-mode media query and uses this instead.
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone
  return displayMode || iosStandalone === true
}

/**
 * "Install UnlimitedTests" banner.
 *
 * Mounted once in the root layout; it self-gates and renders nothing in the
 * overwhelmingly common case. See src/lib/pwa/install-prompt.ts for the rules.
 */
export default function InstallPrompt() {
  const [visible, setVisible] = useState(false)
  const [platform, setPlatform] = useState<InstallPlatform>('unsupported')
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined') return

    let cancelled = false
    let captured: BeforeInstallPromptEvent | null = null
    // Chrome fires `beforeinstallprompt` very early — often before first
    // contentful paint. Without this latch, capturing it would show the banner
    // immediately and the settle delay would only ever apply to iOS, i.e. to
    // the one platform that can't install programmatically anyway.
    let settled = false

    /** Re-run the decision with whatever we know right now. */
    const evaluate = () => {
      if (cancelled) return
      const detected = detectInstallPlatform({
        userAgent: window.navigator.userAgent,
        platform: window.navigator.platform,
        maxTouchPoints: window.navigator.maxTouchPoints,
        hasDeferredPrompt: !!captured,
      })
      setPlatform(detected)
      setVisible(
        settled &&
          shouldShowInstallPrompt({
            platform: detected,
            isStandalone: detectStandalone(),
            sessionActive: isSessionActive(),
            state: readState(),
            nowMs: Date.now(),
          }),
      )
    }

    // NOTE: Chrome can fire `beforeinstallprompt` before this effect runs, in
    // which case we never see it and fall back to showing nothing on Android.
    // Acceptable for now — the event re-fires on subsequent navigations.
    const onBeforeInstallPrompt = (e: Event) => {
      // Suppress Chrome's own mini-infobar so ours is the only ask.
      e.preventDefault()
      captured = e as BeforeInstallPromptEvent
      setDeferred(captured)
      evaluate()
    }

    const onAppInstalled = () => {
      writeState(nextStateAfterInstall(readState(), Date.now()))
      if (!cancelled) setVisible(false)
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)
    window.addEventListener('appinstalled', onAppInstalled)
    // A student who starts a question after the banner appears should see it
    // go away, and one who finishes a module becomes eligible again.
    const unsubscribe = onSessionActiveChange(evaluate)

    // Delay the first look so the banner never lands on first paint.
    const timer = window.setTimeout(() => {
      settled = true
      evaluate()
    }, INSTALL_PROMPT_DELAY_MS)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
      window.removeEventListener('appinstalled', onAppInstalled)
      unsubscribe()
    }
  }, [])

  const dismiss = useCallback(() => {
    writeState(nextStateAfterDismiss(readState(), Date.now()))
    setVisible(false)
  }, [])

  const install = useCallback(async () => {
    if (!deferred) return
    try {
      await deferred.prompt()
      const { outcome } = await deferred.userChoice
      // "dismissed" here is a real refusal of the OS dialog — count it, so a
      // student who declines twice stops being asked.
      if (outcome === 'dismissed') writeState(nextStateAfterDismiss(readState(), Date.now()))
    } catch {
      // A prompt can only be used once; if it throws, just close ours.
    } finally {
      setDeferred(null)
      setVisible(false)
    }
  }, [deferred])

  if (!visible) return null

  return (
    <div
      role="region"
      aria-label="Install UnlimitedTests"
      // Sits clear of the Sage FAB (fixed bottom-4 right-4 z-30): full width
      // above it on mobile, bottom-left beside it on desktop.
      className="fixed inset-x-3 bottom-20 z-30 sm:inset-x-auto sm:bottom-6 sm:left-6 sm:max-w-sm"
    >
      <div className="rounded-xl border border-border bg-card p-4 shadow-lg">
        <div className="flex items-start gap-3">
          <img
            src="/icons/icon-192.png"
            alt=""
            width={40}
            height={40}
            className="mt-0.5 h-10 w-10 shrink-0 rounded-lg"
          />
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-foreground">Add to your home screen</p>
            {platform === 'ios' ? (
              <p className="mt-1 text-sm text-muted-foreground">
                Tap the Share button{' '}
                <Share aria-hidden="true" className="inline h-4 w-4 align-text-bottom" /> below,
                then choose <span className="font-medium text-foreground">Add to Home Screen</span>.
              </p>
            ) : (
              <p className="mt-1 text-sm text-muted-foreground">
                Practice loads faster and works better full-screen.
              </p>
            )}

            {platform === 'installable' && (
              <button
                type="button"
                onClick={install}
                className="mt-3 min-h-[44px] w-full rounded-lg bg-brand px-4 py-2 font-medium text-on-brand transition-colors hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2"
              >
                Install
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss install prompt"
            className="-mr-1 -mt-1 shrink-0 rounded-md p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-brand"
          >
            <X aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
